/**
 * Photo Grid — Featured Image + Thumbnail Carousel + Lightbox
 *
 * Shows a large featured image with a scrollable thumbnail strip below.
 * Clicking a thumbnail updates the featured image.
 * Clicking the featured image opens a full-size lightbox.
 */
(function () {
	'use strict';

	const MODAL_ID = 'photoGridLightbox';
	let items = [];
	let currentIndex = 0;

	// =========================================
	// Featured Image + Thumbnail Sync
	// =========================================

	const initFeaturedGallery = () => {
		const galleries = document.querySelectorAll('.photo-grid');

		galleries.forEach((gallery) => {
			if (gallery.dataset.featuredBound) return;
			gallery.dataset.featuredBound = '1';

			const featured = gallery.querySelector('.photo-grid__featured');
			const featuredImg = gallery.querySelector('.photo-grid__featured-img');
			const thumbItems = gallery.querySelectorAll('.photo-grid__item[data-index]');

			if (!featured || !featuredImg || !thumbItems.length) return;

			// Click thumbnail → update featured image
			thumbItems.forEach((thumb) => {
				thumb.addEventListener('click', () => {
					const idx = parseInt(thumb.dataset.index, 10);
					setActiveThumbnail(gallery, idx);
				});
				thumb.addEventListener('keydown', (e) => {
					if (e.key === 'Enter' || e.key === ' ') {
						e.preventDefault();
						const idx = parseInt(thumb.dataset.index, 10);
						setActiveThumbnail(gallery, idx);
					}
				});
			});

			// Click featured image → open lightbox
			featured.addEventListener('click', () => {
				const idx = parseInt(featured.dataset.index, 10);
				openLightbox(idx);
			});
			featured.addEventListener('keydown', (e) => {
				if (e.key === 'Enter' || e.key === ' ') {
					e.preventDefault();
					openLightbox(parseInt(featured.dataset.index, 10));
				}
			});
		});
	};

	const setActiveThumbnail = (gallery, index) => {
		const featured = gallery.querySelector('.photo-grid__featured');
		const featuredImg = gallery.querySelector('.photo-grid__featured-img');
		const thumbItems = gallery.querySelectorAll('.photo-grid__item[data-index]');
		const targetThumb = gallery.querySelector(`.photo-grid__item[data-index="${index}"]`);

		if (!targetThumb || !featured || !featuredImg) return;

		// Update featured image
		featuredImg.src = targetThumb.dataset.full;
		featuredImg.alt = targetThumb.dataset.alt || '';
		featured.dataset.index = index;
		featured.dataset.full = targetThumb.dataset.full;
		featured.dataset.alt = targetThumb.dataset.alt || '';

		// Update active state on thumbnails
		thumbItems.forEach((t) => t.classList.remove('photo-grid__item--active'));
		targetThumb.classList.add('photo-grid__item--active');
	};

	// =========================================
	// Auto-cycle featured image
	// =========================================

	const initAutoPlay = () => {
		const galleries = document.querySelectorAll('.photo-grid');

		galleries.forEach((gallery) => {
			if (gallery.dataset.autoplayBound) return;
			gallery.dataset.autoplayBound = '1';

			const thumbItems = gallery.querySelectorAll('.photo-grid__item[data-index]');
			if (thumbItems.length < 2) return;

			let intervalId = null;
			const total = thumbItems.length;

			const advance = () => {
				const featured = gallery.querySelector('.photo-grid__featured');
				const cur = parseInt(featured?.dataset.index || '0', 10);
				setActiveThumbnail(gallery, (cur + 1) % total);
			};

			const stop = () => {
				clearInterval(intervalId);
				intervalId = null;
			};

			// Stop auto-cycle permanently on any manual interaction
			gallery.addEventListener('click', (e) => {
				if (e.target.closest('.photo-grid__item, .glider-prev, .glider-next, .photo-grid__featured')) {
					stop();
				}
			});
			gallery.addEventListener('keydown', (e) => {
				if ((e.key === 'Enter' || e.key === ' ') && e.target.closest('.photo-grid__item, .photo-grid__featured')) {
					stop();
				}
			});

			intervalId = setInterval(advance, 3000);
		});
	};

	// =========================================
	// Carousel Init
	// =========================================

	const initCarousels = () => {
		const carousels = document.querySelectorAll('.photo-grid .glider:not(.glider-initialized)');

		carousels.forEach((carousel) => {
			const contain = carousel.closest('.glider-contain');
			if (!contain) return;

			const prevBtn = contain.querySelector('.glider-prev');
			const nextBtn = contain.querySelector('.glider-next');

			carousel.classList.add('glider-initialized');

			new Glider(carousel, {
				slidesToShow: 4,
				slidesToScroll: 3,
				draggable: true,
				arrows: {
					prev: prevBtn,
					next: nextBtn,
				},
				responsive: [
					{
						breakpoint: 0,
						settings: {
							slidesToShow: 4,
							slidesToScroll: 3,
						},
					},
					{
						breakpoint: 576,
						settings: {
							slidesToShow: 6,
							slidesToScroll: 4,
						},
					},
					{
						breakpoint: 768,
						settings: {
							slidesToShow: 8,
							slidesToScroll: 5,
						},
					},
					{
						breakpoint: 992,
						settings: {
							slidesToShow: 10,
							slidesToScroll: 6,
						},
					},
					{
						breakpoint: 1200,
						settings: {
							slidesToShow: 11,
							slidesToScroll: 6,
						},
					},
				],
			});
		});
	};

	// =========================================
	// Lightbox
	// =========================================

	const getOrCreateModal = () => {
		let modal = document.getElementById(MODAL_ID);
		if (modal) return modal;

		modal = document.createElement('div');
		modal.id = MODAL_ID;
		modal.className = 'modal fade photo-grid-lightbox';
		modal.setAttribute('tabindex', '-1');
		modal.setAttribute('aria-hidden', 'true');
		modal.innerHTML =
			'<div class="modal-dialog modal-dialog-centered modal-xl">' +
				'<div class="modal-content bg-transparent border-0 shadow-none">' +
					'<div class="modal-body p-0 text-center">' +
						'<button type="button" class="photo-grid-lightbox__close" data-bs-dismiss="modal" aria-label="Close">' +
							'<svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>' +
						'</button>' +
						'<button type="button" class="photo-grid-lightbox__nav photo-grid-lightbox__prev" aria-label="Previous">' +
							'<svg viewBox="0 0 24 24" width="36" height="36" fill="none" stroke="currentColor" stroke-width="2"><polyline points="15 18 9 12 15 6"/></svg>' +
						'</button>' +
						'<img class="photo-grid-lightbox__image" src="" alt="">' +
						'<button type="button" class="photo-grid-lightbox__nav photo-grid-lightbox__next" aria-label="Next">' +
							'<svg viewBox="0 0 24 24" width="36" height="36" fill="none" stroke="currentColor" stroke-width="2"><polyline points="9 18 15 12 9 6"/></svg>' +
						'</button>' +
						'<p class="photo-grid-lightbox__caption"></p>' +
						'<p class="photo-grid-lightbox__counter"></p>' +
					'</div>' +
				'</div>' +
			'</div>';
		document.body.appendChild(modal);

		modal.querySelector('.photo-grid-lightbox__prev').addEventListener('click', (e) => {
			e.stopPropagation();
			navigate(-1);
		});
		modal.querySelector('.photo-grid-lightbox__next').addEventListener('click', (e) => {
			e.stopPropagation();
			navigate(1);
		});

		modal.addEventListener('keydown', (e) => {
			if (e.key === 'ArrowLeft') navigate(-1);
			else if (e.key === 'ArrowRight') navigate(1);
		});

		let touchStartX = 0;
		modal.addEventListener('touchstart', (e) => {
			touchStartX = e.changedTouches[0].screenX;
		}, { passive: true });
		modal.addEventListener('touchend', (e) => {
			const diff = touchStartX - e.changedTouches[0].screenX;
			if (Math.abs(diff) > 50) navigate(diff > 0 ? 1 : -1);
		}, { passive: true });

		return modal;
	};

	const navigate = (direction) => {
		if (!items.length) return;
		currentIndex = (currentIndex + direction + items.length) % items.length;
		updateLightbox();

		// Also sync the featured image and thumbnail
		const gallery = document.querySelector('.photo-grid');
		if (gallery) {
			setActiveThumbnail(gallery, currentIndex);
		}
	};

	const updateLightbox = () => {
		const modal = document.getElementById(MODAL_ID);
		if (!modal) return;
		const item = items[currentIndex];
		const img = modal.querySelector('.photo-grid-lightbox__image');
		const caption = modal.querySelector('.photo-grid-lightbox__caption');
		const counter = modal.querySelector('.photo-grid-lightbox__counter');
		img.src = item.full;
		img.alt = item.alt;
		caption.textContent = item.alt || '';
		caption.style.display = item.alt ? '' : 'none';
		counter.textContent = (currentIndex + 1) + ' / ' + items.length;
	};

	const openLightbox = (index) => {
		currentIndex = index;
		const modalEl = getOrCreateModal();
		updateLightbox();
		if (typeof bootstrap !== 'undefined' && bootstrap.Modal) {
			bootstrap.Modal.getOrCreateInstance(modalEl).show();
		}
	};

	// =========================================
	// Collect lightbox items
	// =========================================

	const initLightbox = () => {
		const allItems = document.querySelectorAll('.photo-grid__item[data-index]');
		if (!allItems.length) return;

		items = Array.from(allItems).map((el) => ({
			full: el.dataset.full,
			alt: el.dataset.alt || ''
		}));
	};

	// =========================================
	// Init
	// =========================================

	const init = () => {
		initCarousels();
		initLightbox();
		initFeaturedGallery();
		initAutoPlay();
	};

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', init);
	} else {
		init();
	}

	if (window.acf) {
		window.acf.addAction('render_block_preview', init);
	}

	// Observe DOM changes for dynamic loading (front-end only)
	if (document.body && !document.body.classList.contains('block-editor-page')) {
		const observer = new MutationObserver((mutations) => {
			for (const mutation of mutations) {
				if (mutation.addedNodes.length) {
					init();
					break;
				}
			}
		});
		observer.observe(document.body, { childList: true, subtree: true });
	}
})();
